import React from "react";
import { Incident } from "../../models/Incident";
import { computeIncidentWorkedHours } from "../../utils/payrollUtils.ts";
import { usePayrollBranchMeta } from "../../hooks/usePayrollBranchMeta.ts";
import RowDetailIncident from "./RowDetailIncident.tsx";
import { IPayrollReport } from "../../models/Payroll.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import * as moment from "moment-timezone";
import { fetchPayrollPartnerIncidentsForTotal } from "../../utils/payrollTotalIncidents.ts";
import { notification } from "../../utils/Notifications.tsx";

const tableWrapStyle: React.CSSProperties = {
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  overflow: "auto",
  maxHeight: "min(72vh, 720px)",
  boxShadow: "0 4px 16px rgba(185, 28, 28, 0.1)",
  background: "#ffffff",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontSize: "0.8rem",
  marginBottom: 0,
};

const groupThStyle: React.CSSProperties = {
  background: "#dc2626",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "0.72rem",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  textAlign: "center",
  padding: "0.4rem 0.35rem",
  borderBottom: "1px solid #b91c1c",
  borderRight: "none",
  borderLeft: "none",
  whiteSpace: "nowrap",
};

const colThStyle: React.CSSProperties = {
  background: "#fef2f2",
  color: "#7f1d1d",
  fontWeight: 600,
  fontSize: "0.7rem",
  textAlign: "center",
  padding: "0.45rem 0.35rem",
  borderBottom: "2px solid #fecaca",
  borderRight: "none",
  borderLeft: "none",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

/** Bloque Programado (encabezado fila 2). */
const colThProgramadoStyle: React.CSSProperties = {
  ...colThStyle,
  background: "#d1fae5",
  color: "#065f46",
  borderBottom: "2px solid #6ee7b7",
  borderRight: "none",
};

/** Bloque Real (encabezado fila 2). */
const colThRealStyle: React.CSSProperties = {
  ...colThStyle,
  background: "#fef9c3",
  color: "#854d0e",
  borderBottom: "2px solid #fcd34d",
  borderRight: "none",
};

const groupThProgramadoStyle: React.CSSProperties = {
  ...groupThStyle,
  background: "#a7f3d0",
  color: "#14532d",
  borderBottom: "1px solid #6ee7b7",
  borderRight: "none",
};

const groupThRealStyle: React.CSSProperties = {
  ...groupThStyle,
  background: "#fde68a",
  color: "#78350f",
  borderBottom: "1px solid #fbbf24",
  borderRight: "none",
};

const TotalIncidents = ({
  schedulesIds,
  payroll,
  dates,
  partner,
  branch,
  onRefreshPartner,
  onDisplayCountChange,
}: {
  schedulesIds: number[];
  payroll: IPayrollReport;
  dates?: { start: any; end: any };
  partner?: number;
  branch?: number;
  onRefreshPartner?: (partnerId: number) => Promise<void>;
  onDisplayCountChange?: (count: number) => void;
}) => {
  const [incidents, setIncidents] = React.useState<Incident[]>([]);
  const [loading, setLoading] = React.useState(true);

  const payrollBranchMeta = usePayrollBranchMeta(undefined, branch);

  React.useEffect(() => {
    if (partner && branch && dates) {
      fetchIncidents();
    } else {
      setIncidents(getIncidentsFromPayroll());
      setLoading(false);
    }
  }, [
    partner,
    branch,
    schedulesIds?.join(","),
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
  ]);

  const applyWorkedTime = (fetchedIncidents: any[]): any[] => {
    return (fetchedIncidents || []).map((record: any) => ({
      ...record,
      workedTime: computeIncidentWorkedHours(record, { payrollBranchMeta }),
    }));
  };

  const fetchIncidents = async () => {
    if (!partner || !branch || !dates) return;

    setLoading(true);
    try {
      let fetchedIncidents: any[] = [];

      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      fetchedIncidents = await fetchPayrollPartnerIncidentsForTotal({
        partnerId: partner,
        branchId: branch,
        startDate,
        endDate,
        scheduleIds: schedulesIds || [],
      });

      const worked = applyWorkedTime(fetchedIncidents);
      setIncidents(worked);
      onDisplayCountChange?.(worked.length);
    } catch (error) {
      console.error("Error fetching incidents:", error);
      notification("Error ⚠️", "Error al obtener las incidencias", "danger");
      const fallback = getIncidentsFromPayroll();
      setIncidents(fallback);
      onDisplayCountChange?.(fallback.length);
    } finally {
      setLoading(false);
    }
  };

  const getIncidentsFromPayroll = (): Incident[] => {
    if (!payroll.schedules || !Array.isArray(payroll.schedules)) {
      return [];
    }

    const allIncidents: Incident[] = [];
    payroll.schedules.forEach((schedule: any) => {
      if (schedule.incidents && Array.isArray(schedule.incidents)) {
        allIncidents.push(...schedule.incidents);
      }
    });

    if (allIncidents.length === 0) {
      return [];
    }

    let filteredArray = allIncidents;
    if (dates) {
      const startDateUTC = moment
        .utc(dates.start.format("YYYY-MM-DD"))
        .startOf("day");
      const endDateUTC = moment
        .utc(dates.end.format("YYYY-MM-DD"))
        .endOf("day");

      filteredArray = allIncidents.filter((incident) => {
        const incidentDate =
          incident.schedule?.assistanceCheck?.checkDate ||
          incident.schedule?.exactDate;
        if (!incidentDate) return false;
        const incidentDateUTC = moment.utc(incidentDate).startOf("day");
        return (
          incidentDateUTC.isSameOrAfter(startDateUTC, "day") &&
          incidentDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });
    }

    filteredArray.sort((a, b) => {
      const dateAStr =
        a.schedule?.assistanceCheck?.checkDate || a.schedule?.exactDate;
      const dateBStr =
        b.schedule?.assistanceCheck?.checkDate || b.schedule?.exactDate;
      if (!dateAStr || !dateBStr) return 0;
      const dateA = moment.utc(dateAStr).startOf("day");
      const dateB = moment.utc(dateBStr).startOf("day");
      return dateA.valueOf() - dateB.valueOf();
    });

    return applyWorkedTime(filteredArray);
  };

  return (
    <React.Fragment>
      {loading && (
        <div
          style={{
            textAlign: "center",
            padding: "3rem 1rem",
            color: "#64748b",
          }}
        >
          <i
            className="fa fa-spinner fa-spin fa-2x"
            style={{ color: "#0d9488", marginBottom: "0.75rem" }}
          />
          <p style={{ margin: 0, fontWeight: 500 }}>Cargando incidencias...</p>
        </div>
      )}
      {Boolean(!loading && incidents.length > 0) && (
        <div className="incidents-table-wrap" style={tableWrapStyle}>
          <style>{`
            .incidents-table-wrap {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            .incidents-table-wrap::-webkit-scrollbar {
              display: none;
              width: 0;
              height: 0;
            }
            .incidents-schedule-table thead tr:nth-child(2) th:nth-child(4),
            .incidents-schedule-table tbody td:nth-child(4) {
              min-width: 4.5rem;
              overflow: hidden;
              box-sizing: border-box;
              padding-left: 0.2rem;
              padding-right: 0.2rem;
            }
            .incidents-schedule-table thead tr:nth-child(2) th:nth-child(5),
            .incidents-schedule-table tbody td:nth-child(5) {
              min-width: 5.5rem;
              overflow: hidden;
              box-sizing: border-box;
              padding-left: 0.2rem;
              padding-right: 0.2rem;
            }
            .incidents-schedule-table thead tr:nth-child(2) th:nth-child(6),
            .incidents-schedule-table tbody td:nth-child(6) {
              min-width: 0;
              overflow: hidden;
              box-sizing: border-box;
            }
            .incidents-schedule-table > :not(caption) > * > * {
              border-left: none !important;
              border-right: none !important;
            }
            .incidents-schedule-table thead tr:first-child th {
              border-top: none !important;
            }
            .incidents-schedule-table tbody td {
              border-top: none !important;
            }
            .incidents-schedule-table tbody tr:last-child td {
              border-bottom: 1px solid #e2e8f0 !important;
            }
          `}</style>
          <table
            className="table mb-0 incidents-schedule-table"
            style={tableStyle}
          >
            <colgroup>
              <col style={{ width: "7%" }} />
              <col style={{ width: "4.25%" }} />
              <col style={{ width: "4.25%" }} />
              <col style={{ width: "5.25%" }} />
              <col style={{ width: "6.75%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "4%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "10.5%" }} />
              <col style={{ width: "10.5%" }} />
            </colgroup>
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              <tr>
                <th colSpan={1} style={groupThStyle} />
                <th colSpan={2} style={groupThProgramadoStyle}>
                  Programado
                </th>
                <th colSpan={2} style={groupThRealStyle}>
                  Real
                </th>
                <th colSpan={8} style={groupThStyle} />
              </tr>
              <tr>
                <th style={colThStyle}>Fecha</th>
                <th style={colThProgramadoStyle}>Entrada</th>
                <th style={colThProgramadoStyle}>Salida</th>
                <th style={colThRealStyle}>Entrada</th>
                <th style={colThRealStyle}>Salida</th>
                <th style={colThStyle}>Tipo</th>
                <th style={colThStyle}>Sucursal</th>
                <th style={colThStyle}>Tiempo</th>
                <th style={colThStyle}>Razón</th>
                <th style={colThStyle}>Revisado por</th>
                <th style={colThStyle}>Fecha revisión</th>
                <th style={colThStyle}>Acción del líder</th>
                <th style={colThStyle}>Acción final</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((record, index) => (
                <RowDetailIncident
                  key={
                    typeof record.id === "number"
                      ? record.id
                      : String(record.id ?? `row-${index}`)
                  }
                  record={record}
                  payroll={payroll}
                  rowIndex={index}
                  payrollBranchMeta={payrollBranchMeta}
                  onRefreshPartner={onRefreshPartner}
                  onIncidentUpdated={fetchIncidents}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {Boolean(!loading && incidents.length === 0) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2.5rem 1rem",
            borderRadius: "12px",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
          }}
        >
          <h5
            style={{
              margin: 0,
              color: "#64748b",
              fontSize: "1rem",
              fontWeight: 500,
            }}
          >
            No hay incidencias
          </h5>
        </div>
      )}
    </React.Fragment>
  );
};

export default TotalIncidents;
